import { APP_DOMAIN } from '@synergy/config';
import { Box, Stack, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { InternetIcon } from '@synergy/theme';
import { MailIcon, PhoneIcon } from '@synergy/theme-next';
import { PoolImage, PoolLink, PoolRow, PoolTypography } from '@synergy/components-next';
import { ReactElement } from 'react';
import { getImageUrlByCachePath } from '@synergy/utils';

import { imageCachePathsSelector } from '../../../store/configuration-selectors';
import { supplierDetailSelector } from '../../../store/supplier-selectors';
import { useAppSelector } from '../../../../store/store';

const Image = styled(PoolImage)(() => ({
    width: '100%',
    height: 111,
    display: 'block',
    objectFit: 'contain',
}));

const ContainerStack = styled(Stack)(({ theme }) => ({
    background: theme.palette.common.white,
    minWidth: 287,
    maxWidth: 430,
    padding: theme.spacing(3),
    whiteSpace: 'nowrap',
    borderRadius: 4,

    [theme.breakpoints.down('sm')]: {
        height: 'unset',
        width: '100%',
    },
}));

const TextLine = styled('span')(() => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

export const SupplerInfoContactBox = (): ReactElement | null => {
    const supplier = useAppSelector(supplierDetailSelector);
    const imageCachePaths = useAppSelector(imageCachePathsSelector);

    if (!supplier) {
        return null;
    }

    const imageUrl = supplier.image && getImageUrlByCachePath(APP_DOMAIN, '255x109', imageCachePaths, supplier.image);

    // Prepend url protocol if missing. We default to https.
    let website = supplier.url;
    if (website && !website.match(/^[a-zA-Z]+:\/\//)) {
        website = 'https://' + website;
    }

    return (
        <ContainerStack gap={2}>
            <Box sx={{ mt: 2, mb: 3 }}>
                <Image src={imageUrl} alt="logo" />
            </Box>
            <Stack>
                <PoolTypography variant="subtitle1" translationId="app.supplier.headquarters" />
                <TextLine>
                    {[
                        supplier.address.title,
                        supplier.address.street,
                        supplier.address.number,
                        supplier.address.addition,
                    ]
                        .filter(Boolean)
                        .join(' ')}
                </TextLine>
                <TextLine>
                    {supplier.address.zip} {supplier.address.city}
                </TextLine>
                <TextLine>{supplier.address.countryName}</TextLine>
            </Stack>
            <Stack>
                <PoolTypography variant="subtitle1" translationId="app.supplier.contact" />
                {supplier.email && (
                    <PoolRow>
                        <MailIcon />
                        <PoolLink href={`mailto:${supplier.email}`} sx={{ overflow: 'hidden' }}>
                            <TextLine>{supplier.email}</TextLine>
                        </PoolLink>
                    </PoolRow>
                )}
                {supplier.phone && (
                    <PoolRow>
                        <PhoneIcon />
                        <PoolLink href={`tel:${supplier.phone}`} sx={{ overflow: 'hidden' }}>
                            {supplier.phone}
                        </PoolLink>
                    </PoolRow>
                )}
                {website && (
                    <PoolRow>
                        <InternetIcon />
                        <PoolLink href={website} sx={{ overflow: 'hidden' }} target="_blank">
                            {supplier.url}
                        </PoolLink>
                    </PoolRow>
                )}
            </Stack>
            <Stack typography="body2">
                {supplier.uid && (
                    <span>
                        <FormattedMessage id="app.supplier.uid" values={{ value: supplier.uid }} />
                    </span>
                )}
                {supplier.registerNumber && (
                    <span>
                        <FormattedMessage
                            id="app.supplier.registerNumber"
                            values={{ value: supplier.registerNumber }}
                        />
                    </span>
                )}
                {supplier.placeOfJurisdiction && (
                    <span>
                        <FormattedMessage
                            id="app.supplier.placeOfJurisdiction"
                            values={{ value: supplier.placeOfJurisdiction }}
                        />
                    </span>
                )}
            </Stack>
        </ContainerStack>
    );
};
